/**
 * SessionManager
 * ==============
 *
 * Watches the current Cognito access token's expiry and exposes reactive state
 * for a warning modal. Two behaviors:
 *
 *   1. **Silent refresh on activity** — when the user interacts (mouse,
 *      keyboard, API call) within `REFRESH_WINDOW_MS` of expiry, we call
 *      Amplify's `fetchAuthSession({forceRefresh:true})`. Amplify uses the
 *      still-valid refresh token to mint a fresh access token. On success we
 *      reschedule the timers and the user never sees a modal. Most active
 *      sessions stay renewed transparently.
 *
 *   2. **Warning modal + hard logout** — at `T - WARN_LEAD_MS` we flip
 *      `isWarning=true` and start ticking `secondsRemaining` down. A modal
 *      component observes this state and shows a countdown with "Stay signed
 *      in" (triggers refresh) and "Sign out" (immediate logout). At `T - 0`
 *      we force a logout so the user doesn't sit on a page that will 401 on
 *      the next click.
 *
 * State machine is intentionally simple — one authoritative expiry timestamp
 * drives everything. Rescheduling clears any pending timers and re-derives
 * from the new expiry.
 */

import { observable, action, runInAction } from 'mobx';
import { fetchAuthSession } from 'aws-amplify/auth';

// Tunables (module-level for easy tests)
const WARN_LEAD_MS       = 2 * 60 * 1000;      // show warning 2min before expiry
const REFRESH_WINDOW_MS  = 10 * 60 * 1000;     // silent-refresh trigger window
const ACTIVITY_THROTTLE_MS = 30 * 1000;        // at most one refresh attempt / 30s
const TICK_MS            = 1000;               // countdown resolution

class SessionManager {
	@observable isWarning       = false;
	@observable secondsRemaining = 0;

	_expiresAt        = 0;    // ms epoch of access token expiry
	_warnTimer        = null; // setTimeout handle for the warn transition
	_expireTimer      = null; // setTimeout handle for the hard logout
	_countdownTimer   = null; // setInterval handle for the countdown ticker
	_lastRefreshAt    = 0;    // ms epoch of last refresh attempt (throttle)
	_userStore        = null; // injected on init — used for hard logout
	_activityListenersAttached = false;

	/**
	 * Called once at app boot after the user store is available.
	 * Reads the current token expiry from cookies and schedules timers.
	 */
	init (userStore) {
		if (this._userStore) return; // idempotent
		this._userStore = userStore;

		this._scheduleFromCurrentToken();
		this._attachActivityListeners();
	}

	/**
	 * Public: called by the warning modal's "Stay signed in" button and by
	 * the activity handler. Attempts a silent refresh. On success, reschedule
	 * timers from the new expiry. On failure, hard logout.
	 */
	@action
	async refresh () {
		this._lastRefreshAt = Date.now();
		try {
			const session = await fetchAuthSession({ forceRefresh: true });
			const expEpochSec = session?.tokens?.accessToken?.payload?.exp;
			if (!expEpochSec) throw new Error('No exp in refreshed token');

			runInAction(() => {
				this.isWarning       = false;
				this.secondsRemaining = 0;
			});
			this._expiresAt = expEpochSec * 1000;
			this._scheduleTimers();
			return true;
		} catch (err) {
			console.error('[SessionManager] Refresh failed — logging out', err);
			this._hardLogout();
			return false;
		}
	}

	/**
	 * Public: called by the warning modal's "Sign out" button. Same as the
	 * user clicking Logout in the nav.
	 */
	logout () {
		if (this._userStore?.logout) {
			this._userStore.logout();
		} else {
			window.location.replace('/');
		}
	}

	// ── Internal ────────────────────────────────────────────────────────────

	_scheduleFromCurrentToken () {
		const exp = this._readTokenExpiry();
		if (!exp) return;  // not signed in
		this._expiresAt = exp;
		this._scheduleTimers();
	}

	_scheduleTimers () {
		this._clearTimers();

		const now = Date.now();
		const msToWarn   = this._expiresAt - WARN_LEAD_MS - now;
		const msToExpire = this._expiresAt - now;

		if (msToExpire <= 0) {
			this._hardLogout();
			return;
		}

		if (msToWarn <= 0) {
			this._enterWarnState();
		} else {
			this._warnTimer = setTimeout(() => this._enterWarnState(), msToWarn);
		}

		this._expireTimer = setTimeout(() => this._hardLogout(), msToExpire);
	}

	@action
	_enterWarnState () {
		this.isWarning = true;
		this.secondsRemaining = Math.max(0, Math.floor((this._expiresAt - Date.now()) / 1000));

		this._countdownTimer = setInterval(() => {
			runInAction(() => {
				this.secondsRemaining = Math.max(
					0, Math.floor((this._expiresAt - Date.now()) / 1000),
				);
			});
		}, TICK_MS);
	}

	_hardLogout () {
		this._clearTimers();
		runInAction(() => {
			this.isWarning       = false;
			this.secondsRemaining = 0;
		});
		if (this._userStore?.logout) {
			this._userStore.logout();
		} else {
			window.location.replace('/');
		}
	}

	_clearTimers () {
		if (this._warnTimer)      { clearTimeout(this._warnTimer);      this._warnTimer      = null; }
		if (this._expireTimer)    { clearTimeout(this._expireTimer);    this._expireTimer    = null; }
		if (this._countdownTimer) { clearInterval(this._countdownTimer); this._countdownTimer = null; }
	}

	/**
	 * Try to read the Cognito access token from cookies and decode its `exp`
	 * claim. Returns ms epoch, or 0 if no token is present / decodable.
	 */
	_readTokenExpiry () {
		const cookies = document.cookie.split(';').map((c) => c.trim());
		// Any cookie whose name ends with `.accessToken` is the one we want.
		const tokenCookie = cookies.find((c) => c.split('=')[0].endsWith('.accessToken'));
		if (!tokenCookie) return 0;

		const jwt = tokenCookie.split('=').slice(1).join('=');
		try {
			const payload = JSON.parse(atob(jwt.split('.')[1]));
			return (payload.exp || 0) * 1000;
		} catch (err) {
			console.warn('[SessionManager] Could not decode access token', err);
			return 0;
		}
	}

	_attachActivityListeners () {
		if (this._activityListenersAttached) return;
		this._activityListenersAttached = true;

		const onActivity = () => {
			if (!this._expiresAt) return;
			const msLeft = this._expiresAt - Date.now();
			if (msLeft > REFRESH_WINDOW_MS) return;   // too early — do nothing
			if (Date.now() - this._lastRefreshAt < ACTIVITY_THROTTLE_MS) return;
			this.refresh();
		};

		// `passive: true` on scroll/touch to avoid frame drops; other events
		// are fine as default. `once` NOT set — we want continuous coverage.
		window.addEventListener('mousedown', onActivity);
		window.addEventListener('keydown',   onActivity);
		window.addEventListener('scroll',    onActivity, { passive: true });
		window.addEventListener('touchstart', onActivity, { passive: true });
	}
}

// Export a singleton — one session per browser tab.
export default new SessionManager();
